// hooks/useBlitzPayData.js
// FIXED: BBC / Blitz Split invoices now correctly surface in all data flows.
//
// Root issues resolved:
//   1. fetchInvoices (outgoing) — BBC outgoing invoices arrive in `invoices`
//      state but the filteredInvoices filter was cutting them off for non-pending
//      statuses because getPendingOutgoingInvoices only looked for status='pending'.
//      BBC outgoing invoices at 'approved' status still need to be visible.
//
//   2. The `filteredInvoices` derivation now explicitly NEVER filters out BBC
//      invoices by status — they pass through regardless.
//
//   3. Added `bbcInvoices` and `bbcIncomingInvoices` as dedicated derived arrays
//      so BlitzPay.jsx can use them directly without reprocessing.

import { useState, useEffect, useCallback } from "react";
import { useMutation, useQuery } from "react-query";
import client from "../../../API";
import { useIsMounted } from "../../../Hooks/use-is-mounted";

// ── Helper: detect BBC/Blitz Split invoices ──────────────────────────────── //
const isBBCSplit = (inv) =>
  inv?.invoice_type === "bbc" ||
  inv?.source_type  === "bbc" ||
  inv?.isBBC;

export const useBlitzPayData = () => {
  const isMounted = useIsMounted();

  // ── Filter state ─────────────────────────────────────────────────────────
  const [statusFilter, setStatusFilter] = useState("");
  const [creatorFilter, setCreatorFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 365))
      .toISOString()
      .split("T")[0]
  );
  const [endDate, setEndDate] = useState(
    new Date(new Date().setDate(new Date().getDate() + 365))
      .toISOString()
      .split("T")[0]
  );

  // ── Data state ───────────────────────────────────────────────────────────
  const [invoices, setInvoices] = useState([]);
  const [incomingInvoices, setIncomingInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [campaignNames, setCampaignNames] = useState({});
  const [creatorNames, setCreatorNames] = useState({});
  const [allCreatorIds, setAllCreatorIds] = useState([]);
  const [existingCreatorIds, setExistingCreatorIds] = useState([]);
  const [accountBalance, setAccountBalance] = useState("Loading...");
  const [spendingPower, setSpendingPower] = useState("Loading...");
  const [payouts, setPayouts] = useState([]);
  const [isPayoutsLoading, setIsPayoutsLoading] = useState(true);
  const [plaidLinkToken, setPlaidLinkToken] = useState("");

  // ── Team Spending Power ───────────────────────────────────────────────────
  const {
    data: spendingInfo,
    isLoading: spendingLoading,
    error: spendingError,
    refetch: refetchSpending,
  } = useQuery("userSpendingInfo", client.teams.getMySpendingInfo, {
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  // ── Mutations ─────────────────────────────────────────────────────────────
  const { mutate: fetchCompanyData } = useMutation(client.companies.listFetch, {
    onSuccess: (data) => {
      if (data && data.balance !== undefined && data.spending_power_limit !== undefined) {
        setAccountBalance(`$${parseFloat(data.balance).toFixed(2)}`);
        setSpendingPower(
          data.spending_power_limit
            ? `$${parseFloat(data.spending_power_limit).toFixed(2)}`
            : "No Spending Power"
        );
      } else {
        setAccountBalance("Error");
        setSpendingPower("Error");
      }
    },
    onError: () => {
      setAccountBalance("Error");
      setSpendingPower("Error");
    },
  });

  const { mutate: fetchInvoices } = useMutation(client.invoices.list, {
    onSuccess: (data) => {
      setInvoices(data.invoices || []);
      const creatorIdsFromInvoices = (data.invoices || [])
        .filter((inv) => inv.creator_id)
        .map((inv) => inv.creator_id);
      setAllCreatorIds((prev) =>
        Array.from(new Set([...prev, ...creatorIdsFromInvoices]))
      );
    },
    onError: (error) => {
      console.error("Error fetching invoices:", error);
    },
  });

  const { mutate: fetchCampaigns } = useMutation(client.campaigns.list, {
    onSuccess: (data) => {
      const map = {};
      data.forEach((c) => {
        if (c) map[c.id] = c.name;
      });
      setCampaignNames(map);
    },
    onError: (err) => console.error("Error fetching campaigns:", err),
  });

  const { mutate: fetchCreators } = useMutation(client.creators.list, {
    onSuccess: (data) => {
      const map = {};
      const ids = [];
      data.forEach((creator) => {
        if (creator) {
          map[creator.id] = creator.name;
          ids.push(creator.id);
        }
      });
      setCreatorNames(map);
      setExistingCreatorIds(ids);
      setAllCreatorIds((prev) => Array.from(new Set([...prev, ...ids])));
    },
    onError: (err) => console.error("Error fetching creators:", err),
  });

  const { mutate: fetchAllCreatorIds } = useMutation(client.payouts.list, {
    onSuccess: (data) => {
      const uniqueIds = Array.from(
        new Set(data.payouts.map((p) => p.creator_id))
      );
      setAllCreatorIds((prev) => Array.from(new Set([...prev, ...uniqueIds])));
    },
    onError: (err) => console.error("Error fetching creator IDs:", err),
  });

  const { mutate: fetchIncomingInvoices } = useMutation(
    client.invoices.creatorList,
    {
      onSuccess: (data) => {
        setIncomingInvoices(data.incoming_invoices || []);
      },
      onError: (error) => {
        console.error("Error fetching incoming invoices:", error);
      },
    }
  );

  const { mutate: fetchPayouts } = useMutation(client.payouts.list, {
    onSuccess: (data) => {
      setPayouts(data.payouts || []);
    },
    onError: (error) => {
      console.error("Error fetching payouts:", error);
    },
    onSettled: () => {
      setIsPayoutsLoading(false);
    },
  });

  // ── Initial data fetch ────────────────────────────────────────────────────
  useEffect(() => {
    if (!isMounted) return;
    fetchCompanyData();
    fetchInvoices();
    fetchCampaigns();
    fetchCreators();
    fetchAllCreatorIds();
    fetchIncomingInvoices();
    fetchPayouts();
  }, [isMounted]);

  // ── Plaid link token ──────────────────────────────────────────────────────
  useEffect(() => {
    const fetchLinkTokenUser = async () => {
      try {
        const resp = await client.bank.getLinkTokenUser();
        setPlaidLinkToken(resp.link_token);
      } catch (err) {
        console.error("Error fetching user link token:", err);
      }
    };
    fetchLinkTokenUser();
  }, []);

  // ── filteredInvoices derivation ───────────────────────────────────────────
  //
  // KEY FIX: BBC / Blitz Split invoices are NEVER filtered by status here.
  // A BBC invoice at status='approved' (client approved, awaiting settlement)
  // is still actively manageable by the SME and must appear in the activity
  // feed. Only the date range and creator/status filters apply to non-BBC rows.
  //
  // This mirrors the same logic used in BlitzPay.jsx's dedup function: BBC
  // items are always passed through, standard invoices follow normal rules.
  useEffect(() => {
    // DEBUG: log BBC invoices before filtering
    const bbcBeforeFilter = invoices.filter(inv => inv.invoice_type === 'bbc' || inv.source_type === 'bbc' || inv.isBBC);
    console.log('[useBlitzPayData] invoices total:', invoices.length, 'BBC raw:', bbcBeforeFilter.length);
    if (bbcBeforeFilter.length > 0) {
      bbcBeforeFilter.forEach(inv => {
        console.log('[useBlitzPayData] BBC invoice before filter:', {
          id: inv.id, po_number: inv.po_number, invoice_type: inv.invoice_type,
          source_type: inv.source_type, isBBC: inv.isBBC, status: inv.status,
          created_at: inv.created_at, campaign_name: inv.campaign_name,
        });
      });
    }

    setFilteredInvoices(
      invoices.filter((invoice) => {
        const invoiceDate = new Date(invoice.created_at);
        const afterStart  = invoiceDate >= new Date(startDate);
        const beforeEnd   = invoiceDate <= new Date(endDate);
        const inDateRange = afterStart && beforeEnd;

        // BBC outgoing invoices: date range applies, status filter NEVER applies.
        // They remain visible at pending / approved / funded until cancelled/paid.
        if (isBBCSplit(invoice)) {
          const isCancelled = (invoice.status || "").toLowerCase() === "cancelled";
          return inDateRange && !isCancelled;
        }

        // Standard invoices: apply all active filters
        const statusMatch  = statusFilter === "" || invoice.status === statusFilter;
        const creatorMatch = creatorFilter === "" || invoice.creator_id === creatorFilter;
        return inDateRange && statusMatch && creatorMatch;
      })
    );

    // DEBUG: log what made it through the filter
    const bbcAfterFilter = invoices.filter((invoice) => {
      const it = invoice.invoice_type; const st = invoice.source_type;
      return it === 'bbc' || st === 'bbc';
    });
    const bbcSurvived = bbcAfterFilter.filter(inv => {
      const d = new Date(inv.created_at);
      return d >= new Date(startDate) && d <= new Date(endDate) && (inv.status || '').toLowerCase() !== 'cancelled';
    });
    console.log('[useBlitzPayData] filteredInvoices BBC survived filter:', bbcSurvived.length, 'of', bbcAfterFilter.length);
  }, [invoices, statusFilter, creatorFilter, startDate, endDate]);

  // ── Derived BBC slices ────────────────────────────────────────────────────
  // Outgoing BBC: invoices the current company created (live in `invoices`)
  const bbcOutgoingInvoices = invoices.filter(
    (inv) =>
      isBBCSplit(inv) &&
      !["cancelled"].includes((inv.status || "").toLowerCase())
  );

  // Incoming BBC: enterprise sees these in `incomingInvoices`
  // (arrive via /incoming_invoices where client_email = current user's email)
  const bbcIncomingInvoices = incomingInvoices.filter((inv) => isBBCSplit(inv));

  return {
    // ── Filters ──────────────────────────────────────────────────────── //
    statusFilter,
    setStatusFilter,
    creatorFilter,
    setCreatorFilter,
    searchTerm,
    setSearchTerm,
    startDate,
    setStartDate,
    endDate,
    setEndDate,

    // ── Core data ────────────────────────────────────────────────────── //
    invoices,           // all outgoing invoices (raw, unfiltered by date)
    incomingInvoices,   // all incoming invoices (raw)
    filteredInvoices,   // date+status+creator filtered — BBC always included

    // ── BBC slices (pre-derived for BlitzPay.jsx) ─────────────────── //
    bbcOutgoingInvoices,
    bbcIncomingInvoices,

    // ── Lookup maps ──────────────────────────────────────────────────── //
    campaignNames,
    creatorNames,
    allCreatorIds,
    existingCreatorIds,

    // ── Balance ──────────────────────────────────────────────────────── //
    accountBalance,
    spendingPower,

    // ── Payouts ──────────────────────────────────────────────────────── //
    payouts,
    isPayoutsLoading,

    // ── Misc ─────────────────────────────────────────────────────────── //
    plaidLinkToken,
    spendingInfo,
    spendingLoading,
    spendingError,

    // ── Refetch functions ─────────────────────────────────────────────── //
    fetchInvoices,
    fetchIncomingInvoices,
    fetchPayouts,
    refetchSpending,
  };
};